// <PluginViewport> — typed mount slot for plugin canvases.
//
// Closes TP-2 from
// design/30-engineering/ui-implementation/_fix-up-pass-1.md §2.
//
// Host owns lifecycle (mount + unmount); the plugin's mountFn returns an
// unmount callback. Falls back to <EmptyState>-equivalent (here: a tokenized
// inline message) when plugin is missing — typical for the defence variant
// where the Potree-via-plugin is unavailable (boss §1; cross-app pivot to
// the standalone Point Cloud Viewer per cross-surface-pivots.md line 129).

import { useEffect, useRef, type ReactNode } from 'react';
import { tokens } from '../tokens';

/**
 * Plugin mount contract: the host hands the plugin a container and the host's
 * cancel signal; the plugin returns an unmount callback. Plugins are async-
 * safe; the host awaits the returned promise before reporting "mounted".
 */
export type PluginMountFn = (
  container: HTMLDivElement,
  signal: AbortSignal,
) => (() => void) | Promise<() => void>;

export interface PluginViewportProps {
  /** Stable identifier for the plugin slot (e.g. `'potree'`, `'measurement'`). */
  id: string;
  /** Mount function provided by the plugin. When null/undefined → `fallback` renders. */
  mountFn: PluginMountFn | null | undefined;
  /** Fallback rendered when mountFn is missing (defence-variant strip / plugin unavailable). */
  fallback?: ReactNode;
  /** Optional aria-label for the mount region; default 'Plugin viewport'. */
  'aria-label'?: string;
  className?: string;
  'data-testid'?: string;
}

export function PluginViewport({
  id,
  mountFn,
  fallback,
  'aria-label': ariaLabel = 'Plugin viewport',
  className,
  'data-testid': testId,
}: PluginViewportProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!mountFn) return;
    const el = containerRef.current;
    if (!el) return;
    const ctrl = new AbortController();
    let unmount: (() => void) | undefined;
    let cancelled = false;
    void Promise.resolve(mountFn(el, ctrl.signal)).then((dispose) => {
      if (cancelled) {
        dispose?.();
        return;
      }
      unmount = dispose;
    });
    return () => {
      cancelled = true;
      ctrl.abort();
      unmount?.();
    };
  }, [mountFn, id]);

  if (!mountFn) {
    return (
      <div
        data-testid={testId}
        data-plugin-id={id}
        role="region"
        aria-label={ariaLabel}
        className={className}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: tokens.space[5],
          background: tokens.color.canvas.light,
          color: tokens.color.ink.inverse,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.body.size,
          textAlign: 'center',
        }}
      >
        {fallback ?? (
          <span>
            Plugin <code style={{ fontFamily: tokens.font.family.mono }}>{id}</code> is not
            available in this build. See your administrator.
          </span>
        )}
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      data-testid={testId}
      data-plugin-id={id}
      role="region"
      aria-label={ariaLabel}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: tokens.color.canvas.light,
      }}
    />
  );
}
